import { Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { ApiService } from '../../../shared/services/api.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { ConsentFlowService } from '../../../shared/services/consent-flow.service';

const SA_ID_REGEX = /^[0-9]{13}$/;

@Component({
  selector: 'app-data-request',
  templateUrl: './data-request.component.html',
  styleUrls: ['./data-request.component.css']
})
export class DataRequestComponent implements OnInit {
  requestForm: FormGroup;
  readonly maxIdNumbers = 10;
  isSubmitting = false;
  showZeroCreditsBanner = false;
  private estimatedCostPerLookup: number | null = null;
  private estimatedMaxRowsAllowed: number | null = null;
  private hasLoadedEstimate = false;

  constructor(
    private fb: FormBuilder,
    private apiService: ApiService,
    private snackBar: MatSnackBar,
    private router: Router,
    private consentFlowService: ConsentFlowService
  ) {
    this.requestForm = this.fb.group({
      idNumbers: this.fb.array([this.fb.control('', this.saIdValidator)])
    });
  }

  ngOnInit(): void {
    this.preloadUsageEstimate();
  }

  get idNumbers(): FormArray {
    return this.requestForm.get('idNumbers') as FormArray;
  }

  saIdValidator(control: any): { [key: string]: any } | null {
    if (!control.value) {
      return null;
    }
    const value = control.value.toString().trim();
    return SA_ID_REGEX.test(value) ? null : { invalidId: true };
  }

  addIdField(): void {
    if (this.idNumbers.length >= this.maxIdNumbers) {
      this.snackBar.open(`Maximum of ${this.maxIdNumbers} ID numbers allowed`, 'Close', { duration: 3000 });
      return;
    }
    this.idNumbers.push(this.fb.control('', this.saIdValidator));
  }

  removeIdField(index: number): void {
    if (this.idNumbers.length > 1) {
      this.idNumbers.removeAt(index);
    }
  }

  onIdPaste(event: ClipboardEvent): void {
    const clipboardData = event.clipboardData || (window as any).clipboardData;
    if (!clipboardData) {
      return;
    }
    const text = clipboardData.getData('text') || '';
    if (!text) {
      return;
    }

    event.preventDefault();

    // Split by newlines, then by whitespace, to handle Excel column pastes
    const lines = text.split(/\r?\n/);
    const allTokens: string[] = [];
    
    for (const line of lines) {
      // Split by whitespace (spaces, tabs) and add each token
      const tokens = line.split(/\s+/).filter((t: string) => t.trim().length > 0);
      allTokens.push(...tokens);
    }

    // Extract valid 13-digit ID numbers
    const validIds: string[] = [];
    for (const token of allTokens) {
      const trimmed = token.trim();
      // Check if it's exactly 13 digits
      if (/^\d{13}$/.test(trimmed)) {
        validIds.push(trimmed);
        if (validIds.length >= this.maxIdNumbers) break;
      }
    }

    if (!validIds.length) {
      this.snackBar.open('No valid 13-digit ID numbers found in pasted data', 'Close', { duration: 3000 });
      return;
    }

    // Populate the FormArray with parsed IDs (keep all, even duplicates)
    this.idNumbers.clear();
    validIds.forEach((id) => {
      this.idNumbers.push(this.fb.control(id, this.saIdValidator));
    });
  }

  get hasAnyIdNumber(): boolean {
    return this.getNonEmptyIdValues().length > 0;
  }

  async onSubmit(): Promise<void> {
    const idValues = this.getNonEmptyIdValues();
    if (idValues.length === 0) {
      this.snackBar.open('At least one valid ID Number is required', 'Close', { duration: 3000 });
      return;
    }
    const capped = this.applyEstimatedCreditCap(idValues);
    if (capped.allowed.length === 0) {
      return;
    }

    if (!this.requestForm.valid) {
      this.snackBar.open('Please enter valid data before submitting', 'Close', { duration: 3000 });
      return;
    }

    if (this.isSubmitting) {
      return;
    }

    this.isSubmitting = true;
    const submittedCount = capped.allowed.length;
    const singleSubmit = submittedCount === 1;
    const successfulRequestIds: string[] = [];
    const failedById: Array<{ idNumber: string; reason: string }> = [];

    try {
      if (singleSubmit) {
        const consentAccepted = await this.ensureConsentAcceptedForDataAccess();
        if (!consentAccepted) {
          return;
        }
      }

      for (const idNumber of capped.allowed) {
        try {
          const response = await firstValueFrom(
            this.apiService.createDataRequest({
              type: 'ID',
              address: 'ID Numbers',
              idNumbers: [idNumber],
            })
          );
          if (response.requestId) {
            successfulRequestIds.push(response.requestId);
          } else {
            failedById.push({
              idNumber,
              reason: 'No requestId returned by server',
            });
          }
        } catch (error: any) {
          failedById.push({
            idNumber,
            reason: this.toCreateDataRequestErrorMessage(error),
          });
        }
      }

      if (successfulRequestIds.length === 0) {
        this.snackBar.open(
          'There was an error processing your request',
          'Close',
          { duration: 7000 }
        );
        return;
      }

      if (singleSubmit && successfulRequestIds.length === 1) {
        void this.router.navigate(['/admin/data', successfulRequestIds[0]], {
          queryParams: { from: 'user-request' }
        });
        return;
      }

      void this.router.navigate(['/admin/history']);

      if (failedById.length > 0) {
        this.snackBar.open(
          'There was an error processing your request',
          'Close',
          { duration: 7000 }
        );
        return;
      }

      this.snackBar.open(
        `Submitted ${successfulRequestIds.length} request${successfulRequestIds.length === 1 ? '' : 's'}`,
        'Close',
        { duration: 4000 }
      );
    } catch (error) {
      console.error('Failed to submit ID requests:', error);
      this.snackBar.open('An unexpected error occurred while submitting requests', 'Close', { duration: 5000 });
    } finally {
      this.isSubmitting = false;
    }
  }

  private getNonEmptyIdValues(): string[] {
    return this.idNumbers.controls
      .map((c) => (c.value || '').toString().trim())
      .filter((v) => !!v);
  }

  private async ensureConsentAcceptedForDataAccess(): Promise<boolean> {
    return this.consentFlowService.ensureConsentAccepted();
  }

  private toCreateDataRequestErrorMessage(error: any): string {
    let errorMessage = 'Failed to create request';
    const reasonCode = error?.error?.error?.reasonCode;
    if (reasonCode === 'INSUFFICIENT_USER_CREDITS') {
      return 'You do not have enough credits for this request';
    }
    if (error?.error?.error?.message) {
      errorMessage = error.error.error.message;
    }
    return errorMessage;
  }

  private preloadUsageEstimate(): void {
    this.apiService.getBillingUsageSummary().subscribe({
      next: (summary) => {
        const estimate = Number(summary?.estimation?.estimatedCostPerLookup || 0);
        const maxRowsAllowed = Number(summary?.estimation?.maxRowsAllowed);
        this.estimatedCostPerLookup =
          Number.isFinite(estimate) && estimate > 0 ? Math.max(1, Math.trunc(estimate)) : null;
        this.estimatedMaxRowsAllowed =
          Number.isFinite(maxRowsAllowed) && maxRowsAllowed >= 0 ? Math.max(0, Math.trunc(maxRowsAllowed)) : null;
        this.showZeroCreditsBanner = this.estimatedMaxRowsAllowed === 0;
        this.hasLoadedEstimate = true;
      },
      error: () => {
        this.estimatedCostPerLookup = null;
        this.estimatedMaxRowsAllowed = null;
        this.showZeroCreditsBanner = false;
        this.hasLoadedEstimate = true;
      },
    });
  }

  goToAccountTopup(): void {
    void this.router.navigate(['/my-account']);
  }

  private applyEstimatedCreditCap(idValues: string[]): { allowed: string[]; skipped: string[] } {
    if (!this.hasLoadedEstimate || this.estimatedCostPerLookup == null || this.estimatedMaxRowsAllowed == null) {
      return { allowed: idValues, skipped: [] };
    }
    if (idValues.length <= this.estimatedMaxRowsAllowed) {
      return { allowed: idValues, skipped: [] };
    }
    const allowed = idValues.slice(0, this.estimatedMaxRowsAllowed);
    const skipped = idValues.slice(this.estimatedMaxRowsAllowed);
    if (allowed.length === 0) {
      this.snackBar.open(
        `You have insufficient credits for this request.`,
        'Close',
        { duration: 7000 }
      );
      return { allowed, skipped };
    }
    this.snackBar.open(
      `You have insufficient credits. Submitted first ${allowed.length} row${allowed.length === 1 ? '' : 's'}.`,
      'Close',
      { duration: 7000 }
    );
    return { allowed, skipped };
  }
}

